import { ReactElement, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Text } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { toDbId } from "~/utils/graphql-helpers";
import { TypedAGGridCellRenderParams, EscalationRow } from "../types";
import { clickableCell, nonClickableCell } from "./styles_vanilla.css";

type Props = {
  params: TypedAGGridCellRenderParams<EscalationRow>;
};

export const AffectedPositionsRenderer = ({ params }: Props): ReactElement => {
  const history = useHistory();
  const count = params.value as number;
  const hasPositions = params.data?.positionIds && params.data.positionIds.length > 0;

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (hasPositions) {
        // Convert global IDs to database IDs for the URL
        const dbPositionIds = params.data.positionIds.map((globalId) => toDbId(globalId));
        const positionIdsParam = dbPositionIds.join(",");

        // Include arena ID from the escalation case
        const arenaId = params.data.clientId; // This is the arena's objectId (database ID)
        const url = `${globalEnv.complianceRoute}/positions?arenaIds=${arenaId}&positionIds=${positionIdsParam}`;
        history.push(url);
      }
    },
    [history, hasPositions, params.data?.positionIds, params.data?.clientId],
  );

  if (!hasPositions || count === 0) {
    return (
      <Text
        className={nonClickableCell}
        data-testid="affected-positions-non-clickable"
        title="Position not yet created"
      >
        {count}
      </Text>
    );
  }

  return (
    <Text
      className={clickableCell}
      onClick={handleClick}
      data-testid="affected-positions-clickable"
      role="button"
      tabIndex={0}
      title={`Click to view ${count} affected position${count !== 1 ? "s" : ""}`}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick(event as unknown as React.MouseEvent);
        }
      }}
    >
      {count}
    </Text>
  );
};
